import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { adminApi, type PageResult } from '../../api'
import { tablePagination, useServerPagination } from '../../components/ui'
import type { DocLocateImportResult, DocLocateTemplate } from './docLocateShared'

export function DocLocateTemplateList() {
  const navigate = useNavigate()
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [total, setTotal] = useState(0)
  const [rows, setRows] = useState<DocLocateTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<DocLocateTemplate | null>(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [createImportFile, setCreateImportFile] = useState<File | null>(null)
  const [createImportName, setCreateImportName] = useState('')
  const [importResult, setImportResult] = useState<DocLocateImportResult | null>(null)

  const load = useCallback((p: number, ps: number) => {
    setLoading(true)
    return adminApi
      .get<PageResult<DocLocateTemplate>>('/admin/bid-parse/templates', {
        params: { page: p, page_size: ps },
      })
      .then((r) => {
        setRows(r.data.items)
        setTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    void load(page, pageSize)
  }, [page, pageSize, load])

  const openCreate = () => {
    setEditing(null)
    setCreateImportFile(null)
    setCreateImportName('')
    form.resetFields()
    form.setFieldsValue({ sort_no: 0, is_active: true })
    setOpen(true)
  }

  const openEdit = (row: DocLocateTemplate) => {
    setEditing(row)
    form.setFieldsValue({
      template_name: row.template_name,
      description: row.description,
      sort_no: row.sort_no,
      is_active: row.is_active,
    })
    setOpen(true)
  }

  const importFieldsToTemplate = (targetTemplateId: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return adminApi.post<DocLocateImportResult>(
      `/admin/bid-parse/templates/${targetTemplateId}/fields/import`,
      fd,
    )
  }

  const downloadBlankTemplate = () => {
    adminApi
      .get('/admin/bid-parse/fields/import-template', { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data as Blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '文档定位字段模板.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch((e) => message.error(e.message))
  }

  const save = () => {
    void form.validateFields().then((v) => {
      setSaving(true)
      const req = editing
        ? adminApi.put(`/admin/bid-parse/templates/${editing.id}`, {
            template_name: v.template_name,
            description: v.description,
            sort_no: v.sort_no ?? 0,
            is_active: v.is_active ?? true,
          })
        : adminApi.post<{ id: number }>('/admin/bid-parse/templates', v)

      return req
        .then(async (r) => {
          const newId = editing ? editing.id : r.data.id
          if (!editing && createImportFile) {
            const importRes = await importFieldsToTemplate(newId, createImportFile)
            setImportResult(importRes.data)
            const { created, updated, skipped, total_rows } = importRes.data
            if (total_rows === 0 && created === 0 && updated === 0) {
              message.success('模板已创建（Excel 仅表头无数据，可进入模板继续导入）')
            } else {
              message.success(
                `模板已创建并导入字段：新增 ${created}，更新 ${updated}${skipped ? `，跳过 ${skipped}` : ''}`,
              )
            }
          } else {
            message.success('模板已保存')
          }
          setOpen(false)
          setCreateImportFile(null)
          setCreateImportName('')
          await load(page, pageSize)
          if (!editing) navigate(`/manage/doc-locate/${newId}`)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  const deleteTemplate = (row: DocLocateTemplate) => {
    adminApi
      .delete(`/admin/bid-parse/templates/${row.id}`)
      .then(() => {
        message.success('模板已删除')
        void load(page, pageSize)
      })
      .catch((e) => message.error(e.message))
  }

  return (
    <>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        每种文档类型可建独立模板；点击模板名称进入字段维护。文档定位任务需选择对应模板。
      </Typography.Paragraph>

      <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 16 }} onClick={openCreate}>
        新建模板
      </Button>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={[
          {
            title: '模板名称',
            dataIndex: 'template_name',
            render: (name: string, row: DocLocateTemplate) => (
              <Space size="small">
                <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/manage/doc-locate/${row.id}`)}>
                  {name}
                </Button>
                {row.is_builtin ? <Tag>默认</Tag> : null}
              </Space>
            ),
          },
          { title: '说明', dataIndex: 'description', ellipsis: true, render: (v: string) => v || '—' },
          { title: '字段数', dataIndex: 'field_count', width: 80, render: (v?: number) => v ?? 0 },
          { title: '排序', dataIndex: 'sort_no', width: 72 },
          {
            title: '状态',
            dataIndex: 'is_active',
            width: 72,
            render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>),
          },
          {
            title: '操作',
            width: 200,
            render: (_: unknown, row: DocLocateTemplate) => (
              <Space size="small">
                <Button size="small" onClick={() => navigate(`/manage/doc-locate/${row.id}`)}>
                  管理字段
                </Button>
                <Button size="small" onClick={() => openEdit(row)}>
                  编辑
                </Button>
                <Popconfirm
                  title={
                    row.is_builtin
                      ? '删除默认模板？关联的历史定位任务将一并删除；下次使用时会自动重建空默认模板。'
                      : '确定删除该模板及全部定位字段？关联的历史定位任务将一并删除。'
                  }
                  onConfirm={() => deleteTemplate(row)}
                >
                  <Button size="small" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? '编辑模板' : '新建模板'}
        open={open}
        onOk={save}
        onCancel={() => {
          setOpen(false)
          setCreateImportFile(null)
          setCreateImportName('')
        }}
        confirmLoading={saving}
        destroyOnHidden
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="template_name"
            label="模板名称"
            rules={[{ required: true, message: '请输入模板名称' }]}
          >
            <Input placeholder="如 项目合同、采购文件" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} placeholder="适用场景说明（可选）" />
          </Form.Item>
          <Form.Item name="sort_no" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          {!editing ? (
            <Form.Item label="字段 Excel（可选）">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text type="secondary">
                  支持两种 Excel：① 系统字段模板（编码/名称 表头）；② 需求表（A
                  序号、B 字段名、D 提取说明，如 demand.xlsx）。可先
                  <Typography.Link onClick={downloadBlankTemplate} style={{ marginLeft: 4 }}>
                    下载空白模板
                  </Typography.Link>
                  。
                </Typography.Text>
                <Upload
                  accept=".xlsx"
                  maxCount={1}
                  fileList={
                    createImportFile
                      ? [{ uid: '1', name: createImportName || createImportFile.name, status: 'done' }]
                      : []
                  }
                  beforeUpload={(file) => {
                    setCreateImportFile(file)
                    setCreateImportName(file.name)
                    return false
                  }}
                  onRemove={() => {
                    setCreateImportFile(null)
                    setCreateImportName('')
                  }}
                >
                  <Button icon={<UploadOutlined />}>选择 Excel 文件</Button>
                </Upload>
              </Space>
            </Form.Item>
          ) : null}
        </Form>
      </Modal>

      <Modal
        title="导入结果"
        open={!!importResult}
        onCancel={() => setImportResult(null)}
        footer={
          <Button type="primary" onClick={() => setImportResult(null)}>
            知道了
          </Button>
        }
        width={520}
      >
        {importResult ? (
          <>
            <Typography.Paragraph>
              共解析 {importResult.total_rows} 行：新增 {importResult.created}，更新{' '}
              {importResult.updated}
              {importResult.skipped ? `，跳过 ${importResult.skipped}` : ''}
            </Typography.Paragraph>
            {importResult.errors.length ? (
              <>
                <Typography.Text type="secondary">以下行未导入：</Typography.Text>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20, maxHeight: 240, overflow: 'auto' }}>
                  {importResult.errors.map((e) => (
                    <li key={`${e.row}-${e.message}`}>
                      第 {e.row} 行：{e.message}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : null}
      </Modal>
    </>
  )
}
